<script lang="ts" setup>
const route = useRoute();
const runtimeConfig = useRuntimeConfig();
const { locale, locales } = useI18n();
let useClientCountry: any = () => ({
  country: ref(''),
  detectFromCoords: () => {},
  detectFromLanguage: () => {},
});
try {
  const clientCountryModule = await import('~/composables/useClientCountry');
  useClientCountry = clientCountryModule.useClientCountry;
} catch (e) {
  console.error('Failed to load useClientCountry:', e);
}
const { coords, permissionGranted, requestLocation, permissionDenied } = useUserLocation();
const { country, detectFromCoords, detectFromLanguage } = useClientCountry();
const isAutoSelectLocationEnabled = runtimeConfig.public.featureFlags.enableAutoSelectLocation;

// Store Flowbite instance for proper cleanup
let flowbiteInstance: any = null;
let exponeaNotificationModule: any = null;
// Track if component is mounted to prevent operations after unmount
const isMounted = ref(false);
// Debounce timer references for cleanup
const debounceTimers = ref<number[]>([]);
const appRegion = runtimeConfig.public.appRegion;

const popupData = ref<null | {
  targetUrl: string;
  currentSite: string;
  targetSite: string;
}>(null);

declare global {
  interface Window {
    $BV: any;
    bvCallback: (...args: any[]) => any;
    exponea: any;
    monetateQ: any;
    Trustpilot: any;
    gtag: any;
    OneTrust: any;
    flowbiteInstances: any;
    paypal: any;
    braintree: any;
  }
}

// Create a debounce utility to reduce CPU spikes from frequent function calls
const debounce = (fn: (...args: any[]) => void, ms = 300) => {
  let timeoutId: number;
  return function (...args: any[]) {
    clearTimeout(timeoutId);
    timeoutId = window.setTimeout(() => {
      fn(...args);
    }, ms);
    debounceTimers.value.push(timeoutId);
  };
};

useHead({
  link: [
    // Preconnect to CDN url
    {
      rel: 'preconnect',
      href: runtimeConfig.public.assetBaseUrl,
      crossorigin: 'anonymous',
    },
    // href language links
    ...generateHreflangLinks(
      route.fullPath,
      runtimeConfig.public.siteUrl,
      locale.value,
      locales.value
    ),
  ],
});

const DebugPanel = defineAsyncComponent({
  loader: () => import('~/components/global/DebugPanel.vue'),
  delay: 200,
  timeout: 120000,
  errorComponent: {
    template: '<!-- Error loading modal -->',
  },
});

const TsCrossBorderPop = defineAsyncComponent({
  loader: () => import('~/components/TsCrossBorderPop.vue'),
  delay: 200,
  timeout: 120000,
  errorComponent: {
    template: '<!-- Error loading cross border popup -->',
  },
});

const TsModalEcomError = defineAsyncComponent({
  loader: () => import('~/components/TsModalEcomError.vue'),
  delay: 200,
  timeout: 120000,
  errorComponent: {
    template: '<!-- Error loading modal -->',
  },
});

const rootStore = useRootStore();

/**
 * Manually trigger exponea tracking and push notification script by calling `load()` when user consents tracking cookies
 */

const exponeaScript = useExponeaScript({
  trigger: exponeaTrackingConsent,
  warmupStrategy: false,
});

// Use a more efficient approach to handle script loading
if (exponeaScript) {
  exponeaScript.onLoaded(() => {
    if (!isMounted.value) return; // Prevent loading after component unmount

    // Use dynamic import with error handling
    import('~/services-optimized/exponea-notification-service')
      .then(({ checkAvailbility }) => {
        if (!isMounted.value) return; // Safety check
        checkAvailbility();
        exponeaNotificationModule = import('~/services-optimized/exponea-notification-service');
      })
      .catch((error) => {
        console.error('Failed to load exponea notification service:', error);
      });
  });
}

// Memoize the checkout paths to avoid recreating the array on each function call
const CheckoutPaths = Object.freeze(['/trolley', '/checkout']);

// Optimize trolley loading with proper error handling and memoization
const loadTrolley = debounce(async () => {
  if (!isMounted.value) return; // Safety check

  const onCheckout = CheckoutPaths.some((path) => route.fullPath.includes(path));

  /* Set the guest/customer trolley resources only when the header cart link is present in the DOM,
     This is to prevent trolley being loaded on "trolley"/"checkout"/"404" pages and reduce getProducts API calls */
  const headerCartLink = document.getElementById('header-cart-link');
  if (!headerCartLink || onCheckout) return;

  try {
    const { useTrolleyStore } = await import('~/stores/trolley.store');
    if (!isMounted.value) return; // Safety check after async operation

    const trolleyStore = useTrolleyStore();
    /* Set guest or customer trolley when app loads */
    trolleyStore.loadDefaultTrolley();
  } catch (error) {
    console.error('Failed to load trolley:', error);
  }
}, 300);

onMounted(async () => {
  isMounted.value = true;

  if (import.meta.client && isAutoSelectLocationEnabled) {
    requestLocation();
  }

  if (rootStore.debugPanel) {
    runtimeConfig.public.appDebug = true;
  }

  // Awaiting for dynamic store import
  loadTrolley();

  try {
    const { getBrowser } = await import('~/utils/dom.utils');
    if (!isMounted.value) return; // Safety check after async operation

    rootStore.browser = getBrowser();

    const { initFlowbite } = await import('flowbite');
    if (!isMounted.value) return; // Safety check after async operation

    // Use nextTick to ensure DOM is fully rendered before initializing Flowbite
    nextTick(() => {
      if (!isMounted.value) return; // Final safety check

      flowbiteInstance = initFlowbite();
      rootStore.isAppHydrated = true;
      // logStorageUsage(); // To view local and cookie storage consumption to monitor app health
    });
  } catch (error) {
    console.error('Error during app initialization:', error);
  }
});

const freshchatVisibleRoutes = [
  '/content/faq',
  '/contact',
  '/content/delivery',
  '/content/clickandcollect',
  '/content/returnsandexchanges',
  '/guarantee',
  '/content/payment',
];

function initFreshChat() {
  if (appRegion === 'BE') return false; // Freshchat is disabled for Belgium region
  window.fcWidget.init({
    token: 'c047815a-d11b-4359-87b9-20712bd21634',
    host: 'https://toolstationbenelux.freshchat.com',
    widgetUuid: 'f3ca4402-c43b-4fbc-8b32-a05e83ae4646',
  });
}

function initialize(i, t) {
  let e;
  if (i.getElementById(t)) {
    initFreshChat();
  } else {
    e = i.createElement('script');
    e.id = t;
    e.async = true;
    e.src = 'https://toolstationbenelux.freshchat.com/js/widget.js';
    e.onload = initFreshChat;
    i.head.appendChild(e);
  }
}

function initiateCall() {
  initialize(document, 'Freshchat-js-sdk');
}

// Comprehensive cleanup to prevent memory leaks
onBeforeUnmount(() => {
  isMounted.value = false;

  // Clear all debounce timers
  debounceTimers.value.forEach((timerId) => {
    clearTimeout(timerId);
  });
  debounceTimers.value = [];

  // Clean up exponea script event listeners
  if (exponeaScript && typeof (exponeaScript as any).cleanup === 'function') {
    try {
      (exponeaScript as any).cleanup();
    } catch (error) {
      console.error('Error cleaning up exponea script:', error);
    }
  }

  // Clean up any Flowbite initializations
  if (flowbiteInstance) {
    try {
      flowbiteInstance.destroy();
    } catch (error) {
      console.error('Error destroying Flowbite instance:', error);
    }
    flowbiteInstance = null;
  }

  // Clean up exponea notification service module
  if (exponeaNotificationModule) {
    exponeaNotificationModule
      .then((module: any) => {
        if (module && typeof module.cleanup === 'function') {
          try {
            module.cleanup();
          } catch (error) {
            console.error('Error cleaning up exponea notification module:', error);
          }
        }
      })
      .catch(() => {
        // Ignore errors during cleanup
      });
    exponeaNotificationModule = null;
  }
});

onUnmounted(() => {
  /* clear the refresh interval from plugin [refresh-access-token] */
  if (rootStore.accessTokenTimeoutId) clearTimeout(rootStore.accessTokenTimeoutId);
});

const locationProcessed = useCookie('locationProcessed', {
  default: () => false,
  secure: true,
  sameSite: 'lax',
  httpOnly: true,
});

let branchStore: any = null;
try {
  const branchModule = await import('~/stores/branch.store');
  if (branchModule?.useBranchStore) {
    branchStore = branchModule.useBranchStore();
  }
} catch (e) {
  console.error('Failed to load branch store:', e);
}

if (branchStore && !branchStore.lastSavedBranch?.address_id) {
  branchStore.lastSavedBranch = null;
}

const locationChecked = useCookie('locationChecked', {
  default: () => false,
});

const appRegionData = useCookie('appRegion', {
  default: () => '',
});

if (isAutoSelectLocationEnabled) {
  const stopLocationWatch = watch(
    [() => permissionGranted.value, () => permissionDenied.value],
    async ([granted, denied]) => {
      if (!isMounted.value) return;
      const appRegion = runtimeConfig.public.appRegion?.toUpperCase();

      if (appRegion !== appRegionData.value) {
        locationChecked.value = false;
      }

      if (locationChecked.value) return;

      await branchStore.getNearestBranchByCurrentLocation();
      if (granted && coords.value) {
        detectFromCoords(coords.value.lat, coords.value.lng);
      } else {
        detectFromLanguage();
      }

      if (!country.value) {
        stopLocationWatch();
        return;
      }

      locationChecked.value = true;
      appRegionData.value = appRegion;

      if (country.value === 'BE' && appRegion === 'NL') {
        popupData.value = {
          targetUrl: 'https://www.toolstation.be',
          currentSite: 'Toolstation.nl',
          targetSite: 'Toolstation.be',
        };
      }

      if (country.value === 'NL' && appRegion === 'BE') {
        popupData.value = {
          targetUrl: 'https://www.toolstation.nl',
          currentSite: 'Toolstation.be',
          targetSite: 'Toolstation.nl',
        };
      }

      // 🏬 3️⃣ Auto-select nearest branch (only if enabled)
      if (isAutoSelectLocationEnabled && granted && coords.value && !locationProcessed.value) {
        locationProcessed.value = true;

        try {
          const { useBranchStore } = await import('~/stores/branch.store');
          const branchStore = useBranchStore();

          await branchStore.getNearestBranchByCurrentLocation();

          if (branchStore.nearestBranches?.length > 0) {
            const firstNearest = branchStore.nearestBranches[0];
            branchStore.lastSavedBranch = {
              ...firstNearest,
              address_text: removeCountryFromList(firstNearest.address).join(', '),
            };
          }
        } catch (err) {
          console.error('Branch auto-select failed:', err);
        }
      }

      stopLocationWatch();
    }
  );
}

watch(
  () => route.path,
  () => {
    if (!import.meta.client) return;
    if (freshchatVisibleRoutes.includes(route.path)) {
      initiateCall();
    } else {
      // Remove Freshchat widget if present
      if (window.fcWidget && typeof window.fcWidget.destroy === 'function') {
        window.fcWidget.destroy();
      }
    }
  },
  { immediate: true }
);
</script>

<template>
  <div>
    <div v-if="rootStore.isAppHydrated" data-testid="app-hydrated" style="display: none"></div>
    <DebugPanel v-if="rootStore.debugPanel" />
    <NuxtLoadingIndicator />
    <NuxtLayout>
      <TsModalEcomError v-if="rootStore.debugPanel" />
      <NuxtPage />
    </NuxtLayout>
    <TsCrossBorderPop v-if="popupData" v-bind="popupData" @close="popupData = null" />
  </div>
</template>
